import React, { createContext, useContext, useMemo } from 'react';
import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import {
  buildListQuery,
  ListQueryOptions,
  normalizePaginatedListResponse,
  PaginatedApiResponse,
} from '../../services/paginatedList';
import type { SupplyRequisition } from '../../modules/supplies/requisitions/useData';

export type {
  SupplyRequisition,
  SupplyRequisitionLine,
  RequisitionSaveStatus,
  SupplyRequisitionTemplate,
  SupplyRequisitionTemplateLine,
} from '../../modules/supplies/requisitions/useData';

export { useSupplyRequisition } from '../../modules/supplies/requisitions/useData';

const API_LIST = '/supply-requisitions';

const VesselRequisitionsListContext = createContext<number | null>(null);

export function VesselRequisitionsListProvider(props: { vesselId: number; children: React.ReactNode }) {
  const { vesselId, children } = props;
  return React.createElement(VesselRequisitionsListContext.Provider, { value: vesselId }, children);
}

/**
 * List hook for GenericListPage: scopes supply requisitions to the vessel from context via server filter `vesselId`.
 */
export function useVesselRequisitionsListData(
  searchParams?: string,
  page = 1,
  limit = 25,
  options?: ListQueryOptions,
) {
  const vesselId = useContext(VesselRequisitionsListContext);
  if (vesselId == null || !Number.isFinite(vesselId)) {
    throw new Error('useVesselRequisitionsListData requires VesselRequisitionsListProvider with a numeric vesselId');
  }

  const mergedOptions = useMemo((): ListQueryOptions => {
    const prev = options?.filters?.filters ?? {};
    return {
      ...options,
      filters: {
        ...options?.filters,
        filters: {
          ...prev,
          vesselId: [String(vesselId)],
        },
      },
    };
  }, [vesselId, options]);

  const q = buildListQuery(searchParams, page, limit, mergedOptions);
  const { data, error, isLoading, mutate } = useSWR<PaginatedApiResponse<SupplyRequisition> | SupplyRequisition[]>(
    `${API_LIST}${q}`,
    fetcher,
    { revalidateOnFocus: true },
  );
  const normalized = normalizePaginatedListResponse<SupplyRequisition>(data, page, limit);
  return {
    data: normalized.data,
    clients: normalized.data,
    total: normalized.total,
    page: normalized.page,
    limit: normalized.limit,
    isLoading,
    isError: error,
    mutate,
  };
}
